import * as React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Archive, Inbox, Megaphone, MousePointerClick, Pencil, Radar, Trash2 } from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Spinner } from "@/components/ui/spinner";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/ui/empty";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { apiErrorMessage } from "@/lib/api";
import { cn, formatRelativeTime } from "@/lib/utils";
import {
    archiveAffiliate,
    deleteAffiliate,
    getAffiliate,
    getAffiliateAssignmentLog,
    getAffiliateDashboard,
    updateAffiliate,
    type AffiliateDashboardDto,
    type AffiliateDto,
    type UpdateAffiliateRequest,
} from "@/api/affiliates";
import { DealsPanel } from "@/components/deals-panel";
import { listCampaignsPaged } from "@/api/campaigns";
import { listAdSourcesPaged } from "@/api/ad-sources";
import { searchLeads } from "@/api/leads";
import { searchVisits } from "@/api/visits";
import type { LeadDto, VisitDto, AdSourceDto } from "@/types/api";
import type { CampaignDto } from "@/types/campaign";

type TabKey = "overview" | "deals" | "campaigns" | "ad-sources" | "leads" | "visits" | "audit";

const TABS: { key: TabKey; label: string }[] = [
    { key: "overview", label: "Overview" },
    { key: "deals", label: "Deals" },
    { key: "campaigns", label: "Campaigns" },
    { key: "ad-sources", label: "Ad sources" },
    { key: "leads", label: "Leads" },
    { key: "visits", label: "Visits" },
    { key: "audit", label: "Audit log" },
];

const STATUS_VARIANT: Record<string, "success" | "muted" | "warning" | "default"> = {
    active: "success",
    paused: "warning",
    archived: "muted",
};

export function AffiliateDetailPage(): React.ReactNode {
    const { id } = useParams<{ id: string }>();
    const affiliateId = Number(id);
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const confirm = useConfirm();
    const [tab, setTab] = React.useState<TabKey>("overview");
    const [editOpen, setEditOpen] = React.useState(false);

    const affiliateQuery = useQuery({
        queryKey: ["affiliate", affiliateId],
        queryFn: () => getAffiliate(affiliateId),
        enabled: !Number.isNaN(affiliateId),
    });
    const dashboardQuery = useQuery({
        queryKey: ["affiliate", affiliateId, "dashboard"],
        queryFn: () => getAffiliateDashboard(affiliateId),
        enabled: !Number.isNaN(affiliateId),
    });

    const archiveMut = useMutation({
        mutationFn: () => archiveAffiliate(affiliateId),
        onSuccess: () => {
            toast.success("Affiliate archived");
            queryClient.invalidateQueries({ queryKey: ["affiliate", affiliateId] });
            queryClient.invalidateQueries({ queryKey: ["affiliates", "list"] });
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Archive failed")),
    });
    const deleteMut = useMutation({
        mutationFn: () => deleteAffiliate(affiliateId),
        onSuccess: () => {
            toast.success("Affiliate deleted");
            queryClient.invalidateQueries({ queryKey: ["affiliates", "list"] });
            navigate("/affiliates");
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Delete failed — clear FK references first")),
    });

    if (affiliateQuery.isLoading) {
        return <div className="flex min-h-[50vh] items-center justify-center"><Spinner size="lg" /></div>;
    }
    if (!affiliateQuery.data) {
        return (
            <div className="px-6 py-12">
                <EmptyState
                    title="Affiliate not found"
                    description="It may have been deleted. Head back to the list."
                    action={<Button onClick={() => navigate("/affiliates")}>Back to affiliates</Button>}
                />
            </div>
        );
    }
    const aff = affiliateQuery.data;
    const dashboard = dashboardQuery.data;
    const variant = STATUS_VARIANT[aff.status] ?? "default";

    return (
        <>
            <PageHeader
                title={aff.name}
                breadcrumbs={[
                    { label: "Affiliates", href: "/affiliates" },
                    { label: aff.name },
                ]}
                description={
                    <span className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className="font-mono">{aff.slug}</Badge>
                        <Badge variant={variant}>{aff.status}</Badge>
                        {aff.commissionBps != null && <span className="text-muted-foreground">· {formatBps(aff.commissionBps)} commission</span>}
                        {aff.contactEmail && <span className="text-muted-foreground">· {aff.contactEmail}</span>}
                        <span className="text-muted-foreground">· updated {formatRelativeTime(aff.updatedAt)}</span>
                    </span>
                }
                actions={
                    <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => setEditOpen(true)} data-testid="affiliate-edit-button">
                            <Pencil className="h-3.5 w-3.5" /> Edit
                        </Button>
                        {aff.status !== "archived" && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => confirm.show({
                                    title: `Archive "${aff.name}"?`,
                                    description: "Hides the affiliate from active pickers; existing campaigns and ad sources stay attributed.",
                                    confirmLabel: "Archive",
                                    onConfirm: async () => { await archiveMut.mutateAsync(); },
                                })}
                                data-testid="affiliate-archive-button"
                            >
                                <Archive className="h-3.5 w-3.5" /> Archive
                            </Button>
                        )}
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => confirm.show({
                                title: `Delete "${aff.name}"?`,
                                description: "Permanent. Blocked if any campaigns or ad sources still reference this affiliate — reassign or clear those first.",
                                destructive: true,
                                confirmLabel: "Delete affiliate",
                                onConfirm: async () => { await deleteMut.mutateAsync(); },
                            })}
                            data-testid="affiliate-delete-button"
                        >
                            <Trash2 className="h-3.5 w-3.5" /> Delete
                        </Button>
                    </div>
                }
            />

            <div className="space-y-6 px-6 py-4">
                <KpiCards dashboard={dashboard} />

                <div className="border-b border-border">
                    <nav className="flex flex-wrap gap-1" aria-label="Affiliate sections">
                        {TABS.map((t) => (
                            <button
                                key={t.key}
                                type="button"
                                onClick={() => setTab(t.key)}
                                className={cn(
                                    "border-b-2 px-3 py-2 text-sm font-medium transition-colors",
                                    tab === t.key
                                        ? "border-primary text-foreground"
                                        : "border-transparent text-muted-foreground hover:text-foreground"
                                )}
                                data-testid={`affiliate-tab-${t.key}`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </nav>
                </div>

                {tab === "overview" && <OverviewTab aff={aff} dashboard={dashboard} />}
                {tab === "deals" && <DealsPanel side="affiliate" counterpartyId={affiliateId} />}
                {tab === "campaigns" && <CampaignsTab affiliateId={affiliateId} />}
                {tab === "ad-sources" && <AdSourcesTab affiliateId={affiliateId} />}
                {tab === "leads" && <LeadsTab affiliateId={affiliateId} />}
                {tab === "visits" && <VisitsTab affiliateId={affiliateId} />}
                {tab === "audit" && <AuditTab affiliateId={affiliateId} />}
            </div>

            <EditAffiliateDialog
                affiliate={aff}
                open={editOpen}
                onOpenChange={setEditOpen}
                onSaved={() => {
                    queryClient.invalidateQueries({ queryKey: ["affiliate", affiliateId] });
                    queryClient.invalidateQueries({ queryKey: ["affiliates", "list"] });
                }}
            />
            {confirm.dialog}
        </>
    );
}

function formatBps(bps: number | null | undefined): string {
    if (bps == null) return "—";
    return `${(bps / 100).toFixed(2)}%`;
}

function KpiCards({ dashboard }: { dashboard: AffiliateDashboardDto | undefined }): React.ReactNode {
    const cells: { label: string; value: React.ReactNode; accent?: boolean }[] = dashboard ? [
        { label: "Campaigns", value: dashboard.campaignsOwned },
        { label: "Clicks", value: dashboard.clicksAllTime },
        { label: "Leads", value: dashboard.leadsAllTime, accent: true },
        { label: "Conversion rate", value: dashboard.conversionRatePct != null ? `${dashboard.conversionRatePct.toFixed(2)}%` : "—" },
    ] : Array.from({ length: 4 }).map(() => ({ label: "—", value: <span className="text-muted-foreground">—</span> }));
    return (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {cells.map((c, i) => (
                <div
                    key={i}
                    className={cn(
                        "rounded-md border border-border bg-card p-3",
                        c.accent && "border-primary/30 bg-primary/5",
                    )}
                    data-testid={`affiliate-kpi-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{c.label}</div>
                    <div className="mt-1 text-2xl font-semibold tabular-nums">{c.value}</div>
                </div>
            ))}
        </div>
    );
}

function OverviewTab({ aff, dashboard }: { aff: AffiliateDto; dashboard: AffiliateDashboardDto | undefined }): React.ReactNode {
    return (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-md border border-border bg-card p-4">
                <div className="mb-3 text-sm font-medium">Affiliate details</div>
                <dl className="grid grid-cols-3 gap-y-2 text-sm">
                    <dt className="text-muted-foreground">Slug</dt>
                    <dd className="col-span-2 font-mono">{aff.slug}</dd>
                    <dt className="text-muted-foreground">Contact email</dt>
                    <dd className="col-span-2">{aff.contactEmail ?? "—"}</dd>
                    <dt className="text-muted-foreground">Payout email</dt>
                    <dd className="col-span-2">{aff.payoutEmail ?? "—"}</dd>
                    <dt className="text-muted-foreground">Commission</dt>
                    <dd className="col-span-2">{formatBps(aff.commissionBps)}</dd>
                    <dt className="text-muted-foreground">Payout terms</dt>
                    <dd className="col-span-2">{aff.payoutTerms ?? "—"}</dd>
                    <dt className="text-muted-foreground">Status</dt>
                    <dd className="col-span-2">{aff.status}</dd>
                    <dt className="text-muted-foreground">Created</dt>
                    <dd className="col-span-2">{formatRelativeTime(aff.createdAt)}</dd>
                    {aff.archivedAt && (
                        <>
                            <dt className="text-muted-foreground">Archived</dt>
                            <dd className="col-span-2">{formatRelativeTime(aff.archivedAt)}</dd>
                        </>
                    )}
                </dl>
                {aff.notes && (
                    <div className="mt-3 border-t pt-3 text-sm text-muted-foreground whitespace-pre-wrap">{aff.notes}</div>
                )}
            </div>
            <div className="rounded-md border border-border bg-card p-4">
                <div className="mb-3 text-sm font-medium">Performance summary</div>
                {dashboard ? (
                    <ul className="space-y-2 text-sm">
                        <li className="flex items-center justify-between"><span>All-time clicks</span><span className="tabular-nums font-medium">{dashboard.clicksAllTime}</span></li>
                        <li className="flex items-center justify-between"><span>All-time leads</span><span className="tabular-nums font-medium">{dashboard.leadsAllTime}</span></li>
                        <li className="flex items-center justify-between"><span>Conversion rate</span><span className="tabular-nums font-medium">{dashboard.conversionRatePct != null ? `${dashboard.conversionRatePct.toFixed(2)}%` : "—"}</span></li>
                        <li className="flex items-center justify-between"><span>Active campaigns</span><span className="tabular-nums font-medium">{dashboard.campaignsOwned}</span></li>
                    </ul>
                ) : <Spinner size="sm" />}
            </div>
        </div>
    );
}

function CampaignsTab({ affiliateId }: { affiliateId: number }): React.ReactNode {
    const q = useQuery({
        queryKey: ["campaigns", "by-affiliate", affiliateId],
        queryFn: () => listCampaignsPaged({ size: 200, sort: "createdAt,desc", affiliateId }),
    });
    const rows: CampaignDto[] = q.data?.content ?? [];
    if (q.isLoading) return <div className="flex justify-center py-8"><Spinner /></div>;
    if (rows.length === 0) return <EmptyState icon={<Megaphone className="h-6 w-6" />} title="No campaigns attributed" />;
    return (
        <div className="overflow-hidden rounded-md border border-border">
            <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                        <th className="px-3 py-2 text-left">Name</th>
                        <th className="px-3 py-2 text-left">Domain</th>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-left">Created</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-border">
                    {rows.map((c) => (
                        <tr key={c.id} className="hover:bg-muted/30">
                            <td className="px-3 py-2"><Link to={`/campaigns/${c.id}`} className="font-medium hover:underline">{c.name}</Link></td>
                            <td className="px-3 py-2 text-muted-foreground">
                                <Link to={`/domains/${c.domainId}`} className="hover:underline">{c.domainHostname || `#${c.domainId}`}</Link>
                            </td>
                            <td className="px-3 py-2"><Badge variant="outline">{c.status}</Badge></td>
                            <td className="px-3 py-2 text-muted-foreground">{formatRelativeTime(c.createdAt)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function AdSourcesTab({ affiliateId }: { affiliateId: number }): React.ReactNode {
    const q = useQuery({
        queryKey: ["ad-sources", "by-affiliate", affiliateId],
        queryFn: () => listAdSourcesPaged({ size: 200, sort: "slug,asc", affiliateId }),
    });
    const rows: AdSourceDto[] = q.data?.content ?? [];
    if (q.isLoading) return <div className="flex justify-center py-8"><Spinner /></div>;
    if (rows.length === 0) return <EmptyState icon={<Radar className="h-6 w-6" />} title="No ad sources attributed" />;
    return (
        <div className="overflow-hidden rounded-md border border-border">
            <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                        <th className="px-3 py-2 text-left">Slug</th>
                        <th className="px-3 py-2 text-left">Label</th>
                        <th className="px-3 py-2 text-left">Click IDs</th>
                        <th className="px-3 py-2 text-left">Enabled</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-border">
                    {rows.map((s) => (
                        <tr key={s.id} className="hover:bg-muted/30">
                            <td className="px-3 py-2 font-mono">{s.slug}</td>
                            <td className="px-3 py-2 font-medium">{s.label}</td>
                            <td className="px-3 py-2 text-muted-foreground">{s.clickIdParams.join(", ") || "—"}</td>
                            <td className="px-3 py-2">
                                <Badge variant={s.enabled ? "success" : "muted"}>{s.enabled ? "enabled" : "disabled"}</Badge>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function LeadsTab({ affiliateId }: { affiliateId: number }): React.ReactNode {
    const q = useQuery({
        queryKey: ["leads", "by-affiliate", affiliateId],
        queryFn: () => searchLeads({ size: 100, affiliateId }),
    });
    const rows: LeadDto[] = q.data?.content ?? [];
    if (q.isLoading) return <div className="flex justify-center py-8"><Spinner /></div>;
    if (rows.length === 0) return <EmptyState icon={<Inbox className="h-6 w-6" />} title="No leads attributed yet" />;
    return (
        <div className="overflow-hidden rounded-md border border-border">
            <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                        <th className="px-3 py-2 text-left">When</th>
                        <th className="px-3 py-2 text-left">Domain</th>
                        <th className="px-3 py-2 text-left">Campaign</th>
                        <th className="px-3 py-2 text-left">Source</th>
                        <th className="px-3 py-2 text-left">Country</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-border">
                    {rows.map((l) => (
                        <tr key={l.id} className="hover:bg-muted/30">
                            <td className="px-3 py-2"><Link to={`/leads?id=${l.id}`} className="hover:underline">{formatRelativeTime(l.createdAt)}</Link></td>
                            <td className="px-3 py-2 text-muted-foreground">{l.domainHostname ?? `#${l.domainId}`}</td>
                            <td className="px-3 py-2 text-muted-foreground">
                                {l.campaignId ? (
                                    <Link to={`/campaigns/${l.campaignId}`} className="hover:underline">{l.campaignName ?? `#${l.campaignId}`}</Link>
                                ) : "—"}
                            </td>
                            <td className="px-3 py-2 text-muted-foreground">{l.primarySource ?? "—"}</td>
                            <td className="px-3 py-2 text-muted-foreground">{l.country ?? "—"}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function VisitsTab({ affiliateId }: { affiliateId: number }): React.ReactNode {
    const q = useQuery({
        queryKey: ["visits", "by-affiliate", affiliateId],
        queryFn: () => searchVisits({ size: 100, affiliateId }),
    });
    const rows: VisitDto[] = q.data?.content ?? [];
    if (q.isLoading) return <div className="flex justify-center py-8"><Spinner /></div>;
    if (rows.length === 0) return <EmptyState icon={<MousePointerClick className="h-6 w-6" />} title="No visits attributed yet" />;
    return (
        <div className="overflow-hidden rounded-md border border-border">
            <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                        <th className="px-3 py-2 text-left">When</th>
                        <th className="px-3 py-2 text-left">Domain</th>
                        <th className="px-3 py-2 text-left">Campaign</th>
                        <th className="px-3 py-2 text-left">Source</th>
                        <th className="px-3 py-2 text-left">Converted</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-border">
                    {rows.map((v) => (
                        <tr key={v.id} className="hover:bg-muted/30">
                            <td className="px-3 py-2 text-muted-foreground">{formatRelativeTime(v.createdAt)}</td>
                            <td className="px-3 py-2 text-muted-foreground">{v.domainHostname ?? `#${v.domainId}`}</td>
                            <td className="px-3 py-2 text-muted-foreground">
                                {v.campaignId ? (
                                    <Link to={`/campaigns/${v.campaignId}`} className="hover:underline">{v.campaignName ?? `#${v.campaignId}`}</Link>
                                ) : "—"}
                            </td>
                            <td className="px-3 py-2 text-muted-foreground">{v.primarySource ?? "—"}</td>
                            <td className="px-3 py-2">
                                {v.convertedLeadId ? (
                                    <Link to={`/leads?id=${v.convertedLeadId}`} className="text-emerald-600 hover:underline">→ lead #{v.convertedLeadId}</Link>
                                ) : <span className="text-muted-foreground">—</span>}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function AuditTab({ affiliateId }: { affiliateId: number }): React.ReactNode {
    const q = useQuery({
        queryKey: ["affiliate", affiliateId, "assignment-log"],
        queryFn: () => getAffiliateAssignmentLog(affiliateId, 200),
    });
    const rows = q.data ?? [];
    if (q.isLoading) return <div className="flex justify-center py-8"><Spinner /></div>;
    if (rows.length === 0) {
        return <EmptyState title="No assignment history yet" description="Rows show up here every time an admin reassigns a campaign or ad source." />;
    }
    return (
        <div className="overflow-hidden rounded-md border border-border">
            <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                        <th className="px-3 py-2 text-left">When</th>
                        <th className="px-3 py-2 text-left">Entity</th>
                        <th className="px-3 py-2 text-left">Change</th>
                        <th className="px-3 py-2 text-left">Admin</th>
                        <th className="px-3 py-2 text-left">Reason</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-border">
                    {rows.map((row) => {
                        const link = entityLink(row.entityType, row.entityId);
                        const direction = directionForLog(row.oldOwnerId, row.newOwnerId, affiliateId);
                        return (
                            <tr key={row.id} className="hover:bg-muted/30">
                                <td className="px-3 py-2 text-muted-foreground">{formatRelativeTime(row.changedAt)}</td>
                                <td className="px-3 py-2">
                                    <Badge variant="outline" className="font-mono text-[10px] uppercase">{row.entityType}</Badge>
                                    {link ? (
                                        <Link to={link} className="ml-2 text-sm hover:underline">#{row.entityId}</Link>
                                    ) : (
                                        <span className="ml-2 text-sm text-muted-foreground">#{row.entityId}</span>
                                    )}
                                </td>
                                <td className="px-3 py-2">
                                    <Badge variant={direction === "assigned" ? "success" : direction === "removed" ? "warning" : "muted"}>
                                        {direction}
                                    </Badge>
                                </td>
                                <td className="px-3 py-2 text-muted-foreground">{row.changedByEmail ?? `admin #${row.changedByAdminId}`}</td>
                                <td className="px-3 py-2 text-muted-foreground">{row.reason ?? "—"}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function directionForLog(oldOwnerId: number | null, newOwnerId: number | null, focus: number): "assigned" | "removed" | "transferred" {
    if (newOwnerId === focus && oldOwnerId !== focus) return "assigned";
    if (oldOwnerId === focus && newOwnerId !== focus) return "removed";
    return "transferred";
}

function entityLink(entityType: string, entityId: number): string | null {
    switch (entityType) {
        case "campaign": return `/campaigns/${entityId}`;
        case "ad_source": return "/ad-sources";
        default: return null;
    }
}

function EditAffiliateDialog({
    affiliate,
    open,
    onOpenChange,
    onSaved,
}: {
    affiliate: AffiliateDto;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSaved: () => void;
}): React.ReactNode {
    const [name, setName] = React.useState(affiliate.name);
    const [contactEmail, setContactEmail] = React.useState(affiliate.contactEmail ?? "");
    const [payoutEmail, setPayoutEmail] = React.useState(affiliate.payoutEmail ?? "");
    const [commissionPct, setCommissionPct] = React.useState(affiliate.commissionBps != null ? String(affiliate.commissionBps / 100) : "");
    const [payoutTerms, setPayoutTerms] = React.useState(affiliate.payoutTerms ?? "");
    const [notes, setNotes] = React.useState(affiliate.notes ?? "");
    const [status, setStatus] = React.useState(affiliate.status);

    React.useEffect(() => {
        if (open) {
            setName(affiliate.name);
            setContactEmail(affiliate.contactEmail ?? "");
            setPayoutEmail(affiliate.payoutEmail ?? "");
            setCommissionPct(affiliate.commissionBps != null ? String(affiliate.commissionBps / 100) : "");
            setPayoutTerms(affiliate.payoutTerms ?? "");
            setNotes(affiliate.notes ?? "");
            setStatus(affiliate.status);
        }
    }, [open, affiliate]);

    const saveMut = useMutation({
        mutationFn: (req: UpdateAffiliateRequest) => updateAffiliate(affiliate.id, req),
        onSuccess: () => {
            toast.success("Saved");
            onOpenChange(false);
            onSaved();
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Save failed")),
    });

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="affiliate-edit-dialog">
                <DialogHeader>
                    <DialogTitle>Edit affiliate</DialogTitle>
                    <DialogDescription>Slug is immutable — create a new affiliate if you need a different one.</DialogDescription>
                </DialogHeader>
                <form
                    className="space-y-4"
                    onSubmit={(e) => {
                        e.preventDefault();
                        const pctNum = commissionPct.trim() === "" ? null : Number.parseFloat(commissionPct);
                        const bps = pctNum != null && Number.isFinite(pctNum) ? Math.round(pctNum * 100) : null;
                        saveMut.mutate({
                            name: name.trim() || null,
                            contactEmail: contactEmail.trim() || null,
                            payoutEmail: payoutEmail.trim() || null,
                            commissionBps: bps,
                            payoutTerms: payoutTerms.trim() || null,
                            notes: notes.trim() || null,
                            status,
                        });
                    }}
                >
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label htmlFor="aff-edit-name">Name</Label>
                            <Input id="aff-edit-name" value={name} onChange={(e) => setName(e.target.value)} required />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="aff-edit-status">Status</Label>
                            <select
                                id="aff-edit-status"
                                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                            >
                                <option value="active">active</option>
                                <option value="paused">paused</option>
                                <option value="archived">archived</option>
                            </select>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label htmlFor="aff-edit-contact">Contact email</Label>
                            <Input id="aff-edit-contact" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="aff-edit-payout">Payout email</Label>
                            <Input id="aff-edit-payout" type="email" value={payoutEmail} onChange={(e) => setPayoutEmail(e.target.value)} />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label htmlFor="aff-edit-comm">Commission %</Label>
                            <Input
                                id="aff-edit-comm"
                                type="number"
                                step="0.01"
                                min="0"
                                max="100"
                                value={commissionPct}
                                onChange={(e) => setCommissionPct(e.target.value)}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="aff-edit-terms">Payout terms</Label>
                            <Input id="aff-edit-terms" value={payoutTerms} onChange={(e) => setPayoutTerms(e.target.value)} />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="aff-edit-notes">Notes</Label>
                        <Textarea id="aff-edit-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={saveMut.isPending}>Cancel</Button>
                        <Button type="submit" disabled={saveMut.isPending} data-testid="affiliate-edit-submit">
                            {saveMut.isPending ? <Spinner size="sm" /> : "Save changes"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
